import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import UnitScopeTabs from "../../components/UnitScopeTabs";
import { Search, Trash2, Eye, X } from "lucide-react";
import { toast } from "sonner";
import UltimasCompras from "../../components/UltimasCompras";

export default function ClientesMaster() {
  const [unidades, setUnidades] = useState([]);
  const [scope, setScope] = useState("global");
  const [lista, setLista] = useState([]);
  const [busca, setBusca] = useState("");
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    api.get("/unidades").then((r) => setUnidades(r.data));
  }, []);

  const load = () => {
    const params = { with_compras: true };
    if (scope !== "global") params.unidade_id = scope;
    api.get("/clientes", { params }).then((r) => setLista(r.data));
  };

  useEffect(() => {
    load();
  }, [scope]); // eslint-disable-line

  const filtrados = lista.filter((c) => {
    const q = busca.toLowerCase();
    return (
      c.nome.toLowerCase().includes(q) ||
      c.placa.toLowerCase().includes(q) ||
      (c.telefone || "").includes(q) ||
      (c.celular || "").includes(q) ||
      (c.cpf || "").includes(q) ||
      (c.modelo_veiculo || "").toLowerCase().includes(q)
    );
  });

  const excluir = async (c) => {
    if (!window.confirm(`Excluir o cliente ${c.nome}?`)) return;
    await api.delete(`/clientes/${c.id}`);
    toast.success("Cliente excluído");
    load();
  };

  const unitName = (uid) => unidades.find((u) => u.id === uid)?.nome || "—";

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Base consolidada
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Clientes
        </h2>
      </div>

      <UnitScopeTabs unidades={unidades} value={scope} onChange={setScope} />

      <div className="bg-white border-2 border-neutral-900">
        <div className="p-3 border-b-2 border-neutral-900 relative">
          <Search className="w-4 h-4 absolute left-5 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar..."
            className="w-full h-10 pl-8 pr-3 font-mono text-sm focus:outline-none"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-300 bg-neutral-50">
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Nome</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Placa</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">Modelo</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Celular</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden lg:table-cell">CPF</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">Unidade</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c) => (
                <tr key={c.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="p-3 font-display font-bold">{c.nome}</td>
                  <td className="p-3 font-mono text-sm">{c.placa}</td>
                  <td className="p-3 text-sm hidden md:table-cell">{c.modelo_veiculo}</td>
                  <td className="p-3 font-mono text-xs">{c.celular || c.telefone || "—"}</td>
                  <td className="p-3 font-mono text-xs hidden lg:table-cell">{c.cpf || "—"}</td>
                  <td className="p-3 text-sm hidden md:table-cell">{unitName(c.unidade_id)}</td>
                  <td className="p-3 flex gap-1 justify-end">
                    <button
                      data-testid={`master-cliente-view-${c.id}`}
                      onClick={() => setDetalhe(c)}
                      className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`master-cliente-excluir-${c.id}`}
                      onClick={() => excluir(c)}
                      className="p-2 border border-neutral-900 hover:bg-red-600 hover:text-white hover:border-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-neutral-500">
                    Nenhum cliente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detalhe && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  Ficha do cliente
                </p>
                <h3 className="font-display font-black text-xl tracking-tight">
                  {detalhe.nome}
                </h3>
              </div>
              <button
                data-testid="master-cliente-view-close"
                onClick={() => setDetalhe(null)}
                className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Placa" value={detalhe.placa} mono />
                <Info label="Modelo" value={detalhe.modelo_veiculo} />
                <Info label="Celular" value={detalhe.celular || "—"} mono />
                <Info label="Telefone fixo" value={detalhe.telefone || "—"} mono />
                <Info label="CPF" value={detalhe.cpf || "—"} mono />
                <Info label="Unidade" value={unitName(detalhe.unidade_id)} />
                <Info
                  label="KM última troca"
                  value={Number(detalhe.km_ultima_troca || 0).toLocaleString("pt-BR")}
                  mono
                />
                <Info
                  label="Cadastrado em"
                  value={
                    detalhe.created_at
                      ? new Date(detalhe.created_at).toLocaleDateString("pt-BR")
                      : "—"
                  }
                  mono
                />
              </div>
              <div className="border-t border-neutral-200 pt-3">
                <UltimasCompras compras={detalhe.ultimas_compras} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, mono = false }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        {label}
      </div>
      <div className={`${mono ? "font-mono" : "font-display font-bold"} text-sm`}>
        {value}
      </div>
    </div>
  );
}
